import { useState, useEffect, useMemo, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, CheckCircle2, Circle, Clock, X, ExternalLink, Building2, ChevronDown, ChevronUp, ChevronRight, Ticket, ArrowRightCircle, Download } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { usePresalesDashboardStore } from '../store'
import { usePresalesDashboardStore as useCalendarStore } from '@/store'
import { formatCurrency, isHandedOver } from '../utils/dealHelpers'
import { getCommissionDeals, exportCommissionPDF } from '../utils/commissionExport'
import { useTeamMembers, useKeyPersonOptions, useTeamMemberOptions } from '../utils/taskHelpers'
import { TaskCreateForm } from './TaskCreateForm'
import { TaskEditForm } from './TaskEditForm'
import { KanbanColumn } from './KanbanBoard'
import { WaitingJiraView } from './WaitingJiraView'
import { ReassignProspectDialog } from './ReassignProspectDialog'
import type { PresalesDeal, PresalesTask, PresalesTaskStatus } from '@/api/types'

interface PersonTabProps {
  personName: string
}

type SortColumn = 'deal' | 'stage' | 'days' | 'scope' | 'hours' | 'status' | null
type SortDirection = 'asc' | 'desc'
type DealsTab = 'current' | 'handover'

export function PersonTab({ personName }: PersonTabProps) {
  const navigate = useNavigate()
  const {
    deals,
    isLoadingDeals,
    personTasks,
    isLoadingPersonTasks,
    loadPersonTasks,
    createTask,
    togglePersonTaskStatus,
    deletePersonTask,
    updatePersonTask,
  } = usePresalesDashboardStore()

  const { eventLinkSummary, fetchEventLinkSummary } = useCalendarStore()

  const getEventLinkData = (dealId: number) => {
    if (!eventLinkSummary) return null
    return eventLinkSummary.by_deal.find((d) => d.deal_id === dealId) || null
  }

  const [showForm, setShowForm] = useState(false)
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null)
  const [showWaitingJira, setShowWaitingJira] = useState(false)
  const [showHandover, setShowHandover] = useState(false)
  const [showHandoverDealTasks, setShowHandoverDealTasks] = useState(false)

  // Quick-add task from deals table
  const [quickAddDealId, setQuickAddDealId] = useState<number | null>(null)

  // Sort state for deals table — default to scope descending (highest at top)
  const [sortBy, setSortBy] = useState<SortColumn>('scope')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Deals tab state
  const [dealsTab, setDealsTab] = useState<DealsTab>('current')
  const [hidePartnerDeals, setHidePartnerDeals] = useState(true)

  // Shared hooks
  const teamMembers = useTeamMembers()
  const keyPersonOptions = useKeyPersonOptions(teamMembers)
  const teamMemberOptions = useTeamMemberOptions(teamMembers)

  // Capitalised display name
  const displayName = personName.charAt(0).toUpperCase() + personName.slice(1)

  // Map first-name-only personName to full name from teamMembers
  const fullPersonName = useMemo(() => {
    const match = teamMembers.find((m) => m.toLowerCase().startsWith(personName.toLowerCase()))
    return match || personName
  }, [teamMembers, personName])

  // Load person's tasks + calendar data on mount / person change
  useEffect(() => {
    loadPersonTasks(personName)
    fetchEventLinkSummary()
  }, [personName, loadPersonTasks, fetchEventLinkSummary])

  // Filter deals for this person
  const personDeals = useMemo(() => {
    return deals.filter(
      (d) =>
        (d.presales_engineer && d.presales_engineer.toLowerCase().includes(personName.toLowerCase())) ||
        (d.solutions_consultant_owner && d.solutions_consultant_owner.toLowerCase().includes(personName.toLowerCase()))
    )
  }, [deals, personName])

  // Commission-eligible deals (Contract Sent or Closed Won)
  const commissionDeals = useMemo(() => getCommissionDeals(personDeals), [personDeals])

  // Split deals into current vs handover (exclude lost, live, non-presales, and optionally partner deals)
  const { currentDeals, handoverDeals } = useMemo(() => {
    const active = personDeals.filter((d) =>
      !(d.is_closed && !d.closed_won) &&
      !(d.dealstage || d.deal_stage || '').toLowerCase().includes('live') &&
      ((d.presales_engineer && d.presales_engineer.trim() !== '') ||
       (d.presales_engagement_date && d.presales_engagement_date.trim() !== '')) &&
      (!hidePartnerDeals || !(d.pipeline || '').toLowerCase().includes('partner'))
    )
    return {
      currentDeals: active.filter((d) => !isHandedOver(d)),
      handoverDeals: active.filter((d) => isHandedOver(d)),
    }
  }, [personDeals, hidePartnerDeals])

  // Map deal_id → scope_lifecycle_status so we can reuse isHandedOver() for tasks,
  // which otherwise only carry the (sticky) sales_handover_completed flag from the backend.
  const dealById = useMemo(() => {
    const map = new Map<number, PresalesDeal>()
    deals.forEach((d) => map.set(d.id, d))
    return map
  }, [deals])

  const isTaskFromHandoverDeal = (t: PresalesTask | (PresalesTask & Record<string, any>)) => {
    const deal = t.deal_id != null ? dealById.get(t.deal_id) : undefined
    if (deal) return isHandedOver(deal)
    // Fall back to the legacy flag the backend attaches to tasks
    return !!(t as any).sales_handover_completed
  }

  const activeTabDeals = dealsTab === 'current' ? currentDeals : handoverDeals

  // Auto-select the person's most recent open deal for the Add Task form
  const defaultDealId = useMemo(() => {
    const openDeals = personDeals.filter((d) => !d.is_closed)
    if (openDeals.length === 0) return undefined
    // Prefer most recently created deal
    return openDeals.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0]?.id
  }, [personDeals])

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortBy(null)
      }
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
  }

  const sortedDeals = useMemo(() => {
    if (!sortBy) return activeTabDeals

    return [...activeTabDeals].sort((a, b) => {
      let cmp = 0
      switch (sortBy) {
        case 'deal':
          cmp = (a.deal_name || '').localeCompare(b.deal_name || '')
          break
        case 'stage':
          cmp = (a.deal_stage || '').localeCompare(b.deal_stage || '')
          break
        case 'days':
          cmp = (a.days_open ?? -1) - (b.days_open ?? -1)
          break
        case 'scope':
          cmp = (a.scope_completion_percent ?? -1) - (b.scope_completion_percent ?? -1)
          break
        case 'hours': {
          const aLink = getEventLinkData(a.id)
          const bLink = getEventLinkData(b.id)
          const aHrs = (aLink?.presales_minutes || 0) || (a.total_presales_hours || 0) * 60
          const bHrs = (bLink?.presales_minutes || 0) || (b.total_presales_hours || 0) * 60
          cmp = aHrs - bHrs
          break
        }
        case 'status': {
          const aVal = a.is_closed ? 2 : isHandedOver(a) ? 1 : 0
          const bVal = b.is_closed ? 2 : isHandedOver(b) ? 1 : 0
          cmp = aVal - bVal
          break
        }
      }
      return sortDirection === 'desc' ? -cmp : cmp
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabDeals, sortBy, sortDirection, eventLinkSummary])

  // Filter out tasks belonging to handover deals (unless toggled on)
  const activeTasks = useMemo(() => {
    return personTasks.filter((t) => !isTaskFromHandoverDeal(t))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personTasks, dealById])

  const handoverDealTasks = useMemo(() => {
    return personTasks.filter((t) => isTaskFromHandoverDeal(t))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personTasks, dealById])

  const todoTasks = activeTasks.filter((t) => t.status === 'open')
  const waitingTasks = activeTasks.filter((t) => t.status === 'waiting')
  const handoverTasks = activeTasks.filter((t) => t.status === 'handover')
  const doneTasks = activeTasks.filter((t) => t.status === 'completed')

  // Handlers
  const handleToggleForm = () => {
    setShowForm(!showForm)
  }

  const handleCreateSubmit = async (dealId: number | null, data: any) => {
    await createTask(dealId, data)
    setShowForm(false)
    loadPersonTasks(personName)
  }

  const handleQuickAddToggle = (dealId: number) => {
    if (quickAddDealId === dealId) {
      setQuickAddDealId(null)
    } else {
      setQuickAddDealId(dealId)
    }
  }

  const handleQuickCreateSubmit = async (dealId: number | null, data: any) => {
    await createTask(dealId, data)
    setQuickAddDealId(null)
    loadPersonTasks(personName)
  }

  const handleStatusChange = (task: PresalesTask, newStatus: PresalesTaskStatus) => {
    togglePersonTaskStatus(task.id, newStatus, personName)
  }

  const handleDelete = (task: PresalesTask) => {
    if (window.confirm('Delete this task?')) {
      deletePersonTask(task.id, personName)
    }
  }

  const handleStartEdit = (task: PresalesTask) => {
    setEditingTaskId(task.id)
  }

  const handleSaveEdit = async (taskId: number, data: any) => {
    await updatePersonTask(taskId, data)
    setEditingTaskId(null)
  }

  const handleCancelEdit = () => {
    setEditingTaskId(null)
  }

  const handleDrop = (taskId: number, newStatus: PresalesTaskStatus) => {
    const task = personTasks.find((t) => t.id === taskId)
    if (task && task.status !== newStatus) {
      handleStatusChange(task, newStatus)
    }
  }

  const openTaskCount = todoTasks.length + waitingTasks.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">{displayName}'s Deals & Tasks</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Deals assigned to {displayName} and their tasks across all deals
        </p>
      </div>

      {/* Deals Table */}
      <Card className="p-5">
        <div className="flex items-center gap-1 mb-4 border-b border-[var(--border-color)]">
          <button
            onClick={() => setDealsTab('current')}
            className={`px-3 py-2 text-sm font-medium transition-colors relative ${
              dealsTab === 'current'
                ? 'text-[var(--text-primary)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
          >
            Current Deals ({currentDeals.length})
            {dealsTab === 'current' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setDealsTab('handover')}
            className={`px-3 py-2 text-sm font-medium transition-colors relative ${
              dealsTab === 'handover'
                ? 'text-[var(--text-primary)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
          >
            Handover ({handoverDeals.length})
            {dealsTab === 'handover' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <div className="flex items-center gap-3 ml-auto">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hidePartnerDeals}
                onChange={() => setHidePartnerDeals(!hidePartnerDeals)}
                className="rounded"
              />
              <span className="text-xs text-[var(--text-secondary)] whitespace-nowrap">Hide partner deals</span>
            </label>
            {commissionDeals.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => exportCommissionPDF(commissionDeals, fullPersonName)}
                title="Export commission-eligible deals (Contract Sent + Closed Won) as PDF"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">Commission PDF</span>
              </Button>
            )}
          </div>
        </div>

        {isLoadingDeals ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : activeTabDeals.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-4">
            {dealsTab === 'current' ? `No current deals for ${displayName}.` : `No handover deals for ${displayName}.`}
          </p>
        ) : (
          <div className="max-h-[440px] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-[var(--bg-secondary)]">
                <tr className="text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider border-b border-[var(--border-color)]">
                  <th className="pb-2 pr-4 max-w-[200px] cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors" onClick={() => handleSort('deal')}>
                    <span className="inline-flex items-center gap-0.5">Deal{sortBy === 'deal' && <span className="text-primary">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>}</span>
                  </th>
                  <th className="pb-2 pr-4 max-w-[100px] cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors" onClick={() => handleSort('stage')}>
                    <span className="inline-flex items-center gap-0.5">Stage{sortBy === 'stage' && <span className="text-primary">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>}</span>
                  </th>
                  <th className="pb-2 pr-4 text-right">Amount</th>
                  <th className="pb-2 pr-4 text-right cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors" onClick={() => handleSort('days')}>
                    <span className="inline-flex items-center justify-end gap-0.5 w-full">Days{sortBy === 'days' && <span className="text-primary">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>}</span>
                  </th>
                  <th className="pb-2 pr-4 text-right cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors" onClick={() => handleSort('scope')}>
                    <span className="inline-flex items-center justify-end gap-0.5 w-full">Scope %{sortBy === 'scope' && <span className="text-primary">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>}</span>
                  </th>
                  <th className="pb-2 pr-4 text-right cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors" onClick={() => handleSort('hours')}>
                    <span className="inline-flex items-center justify-end gap-0.5 w-full">Hours{sortBy === 'hours' && <span className="text-primary">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>}</span>
                  </th>
                  <th className="pb-2 pr-4 cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors" onClick={() => handleSort('status')}>
                    <span className="inline-flex items-center gap-0.5">Status{sortBy === 'status' && <span className="text-primary">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>}</span>
                  </th>
                  <th className="pb-2 text-right">Tasks</th>
                </tr>
              </thead>
              <tbody>
                {sortedDeals.map((deal) => (
                  <Fragment key={deal.id}>
                    <PersonDealRow
                      deal={deal}
                      onClick={() => navigate(`/dashboard/presales/deal/${deal.hubspot_deal_id}`)}
                      onQuickAdd={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        handleQuickAddToggle(deal.id)
                      }}
                      isQuickAddOpen={quickAddDealId === deal.id}
                      getEventLinkData={getEventLinkData}
                    />
                    {quickAddDealId === deal.id && (
                      <tr className="bg-[var(--bg-primary)]">
                        <td colSpan={8} className="px-4 py-3 border-b border-[var(--border-color)]">
                          <div className="text-xs font-medium text-[var(--text-muted)] mb-2">
                            Quick add task — <span className="text-[var(--text-secondary)]">{deal.deal_name}</span>
                          </div>
                          <TaskCreateForm
                            deal={deal}
                            defaultAssignedTo={deal.presales_engineer || fullPersonName}
                            onSubmit={handleQuickCreateSubmit}
                            onCancel={() => setQuickAddDealId(null)}
                            compact
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Early Deals / Prospect Events for this person */}
      <PersonProspectSection personName={fullPersonName} />

      {/* Tasks Kanban Board */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">Tasks</h3>
            {openTaskCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-900/30 text-amber-300 border border-amber-700/40">
                {openTaskCount} open
              </span>
            )}
            {personTasks.length > 0 && openTaskCount === 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-900/30 text-green-300 border border-green-700/40">
                All done
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowWaitingJira(!showWaitingJira)}
              className={showWaitingJira ? 'text-amber-400' : ''}
            >
              <Ticket className="h-4 w-4 mr-1" />
              Jira Tracker
            </Button>
            <Button variant="ghost" size="sm" onClick={handleToggleForm}>
              {showForm ? (
                <>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Task
                </>
              )}
            </Button>
          </div>
        </div>

        {showWaitingJira && (
          <div className="mb-4">
            <WaitingJiraView personName={personName} onClose={() => setShowWaitingJira(false)} />
          </div>
        )}

        {showForm && (
          <div className="mb-4">
            <TaskCreateForm
              defaultAssignedTo={fullPersonName}
              defaultDealId={defaultDealId}
              onSubmit={handleCreateSubmit}
              onCancel={() => setShowForm(false)}
            />
          </div>
        )}

        {isLoadingPersonTasks ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : personTasks.length === 0 && !showForm ? (
          <div className="text-center py-8 text-sm text-[var(--text-muted)]">
            No tasks assigned to {displayName} yet.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4">
              {/* To Do Column */}
              <KanbanColumn
                title="To Do"
                count={todoTasks.length}
                colorClass="text-blue-400 border-blue-500/40 bg-blue-900/20"
                icon={<Circle className="h-4 w-4" />}
                status="open"
                onDrop={handleDrop}
                tasks={todoTasks}
                onDelete={handleDelete}
                onEdit={handleStartEdit}
              />

              {/* Waiting Column */}
              <KanbanColumn
                title="Waiting"
                count={waitingTasks.length}
                colorClass="text-amber-400 border-amber-500/40 bg-amber-900/20"
                icon={<Clock className="h-4 w-4" />}
                status="waiting"
                onDrop={handleDrop}
                tasks={waitingTasks}
                onDelete={handleDelete}
                onEdit={handleStartEdit}
              />

              {/* Done Column */}
              <KanbanColumn
                title="Done"
                count={doneTasks.length}
                colorClass="text-green-400 border-green-500/40 bg-green-900/20"
                icon={<CheckCircle2 className="h-4 w-4" />}
                status="completed"
                onDrop={handleDrop}
                tasks={doneTasks}
                onDelete={handleDelete}
                onEdit={handleStartEdit}
                collapsible
              />
            </div>

            {/* Handover status toggle */}
            {(handoverTasks.length > 0 || showHandover) && (
              <div className="mt-4">
                <button
                  onClick={() => setShowHandover(!showHandover)}
                  className="flex items-center gap-1.5 text-xs text-purple-400/80 hover:text-purple-300 transition-colors"
                >
                  {showHandover ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  <ArrowRightCircle className="h-3.5 w-3.5" />
                  Handed over to Implementations ({handoverTasks.length})
                </button>

                {showHandover && (
                  <div className="mt-2">
                    <KanbanColumn
                      title="Handover"
                      count={handoverTasks.length}
                      colorClass="text-purple-400 border-purple-500/40 bg-purple-900/20"
                      icon={<ArrowRightCircle className="h-4 w-4" />}
                      status="handover"
                      onDrop={handleDrop}
                      tasks={handoverTasks}
                      onDelete={handleDelete}
                      onEdit={handleStartEdit}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Tasks from handover deals toggle */}
            {handoverDealTasks.length > 0 && (
              <div className="mt-3">
                <button
                  onClick={() => setShowHandoverDealTasks(!showHandoverDealTasks)}
                  className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                >
                  {showHandoverDealTasks ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  Tasks from handover deals ({handoverDealTasks.length})
                </button>

                {showHandoverDealTasks && (
                  <div className="mt-2 space-y-1 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                    {handoverDealTasks.map((task) => (
                      <div
                        key={task.id}
                        onClick={() => setEditingTaskId(task.id)}
                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-sm cursor-pointer hover:border-[var(--text-muted)]/30 transition-colors opacity-70"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`flex-shrink-0 w-2 h-2 rounded-full ${
                            task.status === 'open' ? 'bg-blue-400' : task.status === 'waiting' ? 'bg-amber-400' : task.status === 'handover' ? 'bg-purple-400' : 'bg-green-400'
                          }`} />
                          <span className="truncate text-[var(--text-secondary)]">{task.description}</span>
                        </div>
                        <span className="text-[11px] text-[var(--text-muted)] flex-shrink-0">{task.deal_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Edit Task Modal */}
        {editingTaskId && (() => {
          const editingTask = personTasks.find((t) => t.id === editingTaskId)
          if (!editingTask) return null
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleCancelEdit}>
              <div className="absolute inset-0 bg-black/60" />
              <div className="relative z-10 w-full max-w-5xl mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)]">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">Edit Task</h3>
                    <button onClick={handleCancelEdit} className="p-1 rounded hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="p-5">
                    <TaskEditForm
                      task={editingTask}
                      teamMemberOptions={teamMemberOptions}
                      keyPersonOptions={keyPersonOptions}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

// ==================== Person Prospect Section ====================

const PROSPECT_COLLAPSED_LIMIT = 3

function PersonProspectSection({ personName }: { personName: string }) {
  const { eventLinkSummary, reassignProspectEvents } = useCalendarStore()
  const [reassigningCompany, setReassigningCompany] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  // Find prospect entries for this person
  const personData = eventLinkSummary?.by_person.find((p) =>
    p.person.toLowerCase() === personName.toLowerCase() ||
    personName.toLowerCase().startsWith(p.person.toLowerCase()) ||
    p.person.toLowerCase().startsWith(personName.split(' ')[0].toLowerCase())
  )

  const prospects = personData?.by_prospect || []
  if (prospects.length === 0) return null

  const totalMinutes = prospects.reduce((sum, p) => sum + p.presales_minutes, 0)
  const canExpand = prospects.length > PROSPECT_COLLAPSED_LIMIT
  const visibleProspects = expanded ? prospects : prospects.slice(0, PROSPECT_COLLAPSED_LIMIT)

  const handleReassign = async (prospectCompany: string, dealId: number) => {
    await reassignProspectEvents(prospectCompany, dealId)
    setReassigningCompany(null)
  }

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wide">
          Early Deals
        </h3>
        <span className="text-xs text-[var(--text-muted)]">
          {(totalMinutes / 60).toFixed(1)}h unattributed
        </span>
      </div>

      <div className="space-y-2">
        {visibleProspects.map((prospect) => (
          <div
            key={prospect.prospect_company}
            className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-600/20"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span>🏢</span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-amber-400/90 truncate">
                  {prospect.prospect_company}
                </div>
                <div className="text-xs text-[var(--text-muted)]">
                  {prospect.event_count} event{prospect.event_count !== 1 ? 's' : ''} · {(prospect.presales_minutes / 60).toFixed(1)}h
                </div>
              </div>
            </div>

            {reassigningCompany === prospect.prospect_company ? (
              <div className="flex-1 ml-3 max-w-[320px]">
                <ReassignProspectDialog
                  prospectCompany={prospect.prospect_company}
                  onReassign={(dealId) => handleReassign(prospect.prospect_company, dealId)}
                  onCancel={() => setReassigningCompany(null)}
                />
              </div>
            ) : (
              <button
                onClick={() => setReassigningCompany(prospect.prospect_company)}
                className="text-xs text-[var(--button-primary-bg)] hover:text-[var(--button-primary-hover)] transition-colors whitespace-nowrap ml-3"
              >
                Reassign to Deal →
              </button>
            )}
          </div>
        ))}
      </div>

      {canExpand && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              Show all {prospects.length} prospects
            </>
          )}
        </button>
      )}
    </Card>
  )
}

// ==================== Person Deal Row ====================

function PersonDealRow({
  deal,
  onClick,
  onQuickAdd,
  isQuickAddOpen,
  getEventLinkData,
}: {
  deal: PresalesDeal
  onClick: () => void
  onQuickAdd: (e: React.MouseEvent) => void
  isQuickAddOpen: boolean
  getEventLinkData: (dealId: number) => { presales_minutes: number; frankieone_total_minutes: number; demo_count: number } | null
}) {
  const openCount = deal.task_count_open || 0
  const totalCount = deal.task_count_total || 0
  const isClosed = !!deal.is_closed

  // Hours calculation
  const linkData = getEventLinkData(deal.id)
  const presalesMins = linkData?.presales_minutes || 0
  const f1Mins = linkData?.frankieone_total_minutes || 0
  const presalesHrs = (presalesMins / 60).toFixed(1)
  const f1Hrs = (f1Mins / 60).toFixed(1)
  const hasCalendarHours = presalesMins > 0 || f1Mins > 0

  return (
    <tr
      onClick={onClick}
      className="border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)] cursor-pointer transition-colors group"
    >
      <td className="py-2.5 pr-4 max-w-[200px]">
        <div className="flex items-center gap-1.5">
          <span className={`font-medium ${isClosed ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]'}`}>
            {deal.deal_name}
          </span>
          {deal.dealhub_deal_id && (
            <a
              href={`/scope?session=${deal.dealhub_deal_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 p-0.5 rounded text-[var(--text-muted)] hover:text-primary opacity-0 group-hover:opacity-100 transition-all"
              title="Open SOW"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {isClosed && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ml-0.5 inline-block ${deal.closed_won ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'}`}>
              {deal.closed_won ? 'Won' : 'Lost'}
            </span>
          )}
        </div>
      </td>
      <td className="py-2.5 pr-4 text-[var(--text-muted)] max-w-[100px] truncate">{deal.deal_stage || '-'}</td>
      <td className="py-2.5 pr-4 text-right text-[var(--text-muted)]">{formatCurrency(deal.amount)}</td>
      <td className="py-2.5 pr-4 text-right text-[var(--text-muted)]">{deal.days_open ?? '-'}</td>
      <td className="py-2.5 pr-4 text-right">
        {deal.scope_completion_percent != null ? (
          <span className={deal.scope_completion_percent >= 80 ? 'text-green-400' : deal.scope_completion_percent >= 40 ? 'text-amber-400' : 'text-[var(--text-muted)]'}>
            {deal.scope_completion_percent}%
          </span>
        ) : (
          <span className="text-[var(--text-muted)]">-</span>
        )}
      </td>
      <td className="py-2.5 pr-4 text-right whitespace-nowrap">
        {hasCalendarHours ? (
          <span className="text-[var(--text-secondary)]" title={`Presales: ${presalesHrs}h / FrankieOne: ${f1Hrs}h`}>
            {presalesHrs}<span className="text-[var(--text-muted)]">/</span>{f1Hrs}h
          </span>
        ) : deal.total_presales_hours > 0 ? (
          <span className="text-[var(--text-muted)]">{deal.total_presales_hours}h</span>
        ) : (
          <span className="text-[var(--text-muted)]">-</span>
        )}
      </td>
      <td className="py-2.5 pr-4">
        {isClosed ? (
          <span className="text-[var(--text-muted)]">-</span>
        ) : isHandedOver(deal) ? (
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-900/30 text-green-300 border border-green-700/40">
            Implementations
          </span>
        ) : (
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-900/30 text-amber-300 border border-amber-700/40">
            Presales
          </span>
        )}
      </td>
      <td className="py-2.5 text-right">
        <div className="flex items-center justify-end gap-2">
          {totalCount > 0 ? (
            <span className={`text-xs font-medium ${openCount > 0 ? 'text-amber-300' : 'text-green-400'}`}>
              {openCount}/{totalCount}
            </span>
          ) : (
            <span className="text-[var(--text-muted)]">-</span>
          )}
          <button
            onClick={onQuickAdd}
            className={`p-0.5 rounded transition-colors ${
              isQuickAddOpen
                ? 'text-primary bg-primary/10'
                : 'text-[var(--text-muted)] hover:text-primary opacity-0 group-hover:opacity-100'
            }`}
            title="Quick add task"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}

// ==================== Person Task Row ====================

// PersonTaskRow removed — replaced by KanbanTaskCard
